import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Package, CreditCard, LogOut, ChevronRight, Save, MapPin, Phone, Mail, Heart, Search, Download, AlertCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { toast } from "sonner";
import { CustomerCredits } from "../components/CustomerCredits";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CustomerAccount() {
  const { customer, loading, logout, refreshCustomer, guestBookingCount, guestPromptDismissed, dismissGuestPrompt, importAllGuestBookings } = useCustomerAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({ name: "", phone: "", billing_address: "", shipping_address: "" });
  const [importingAll, setImportingAll] = useState(false);
  const [manualBookingId, setManualBookingId] = useState("");
  const [manualImporting, setManualImporting] = useState(false);
  const [manualError, setManualError] = useState("");

  useEffect(() => {
    if (!loading && !customer) navigate("/login", { replace: true });
    if (customer) {
      setProfile({
        name: customer.name || "",
        phone: customer.phone || "",
        billing_address: customer.billing_address || "",
        shipping_address: customer.shipping_address || "",
      });
    }
  }, [customer, loading, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("mahra_session_token");
      const res = await fetch(`${API}/api/customer/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(profile),
      });
      if (!res.ok) throw new Error("Failed to save");
      await refreshCustomer();
      toast.success("Profile updated");
    } catch {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  if (loading || !customer) return null;

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <Navbar />
      <div className="max-w-[900px] mx-auto px-4 lg:px-8 py-10 lg:py-16">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-[10px] text-[#9A7B4F] tracking-[0.2em] uppercase mb-1">My Account</p>
            <h1 className="text-2xl lg:text-3xl text-[#282828] font-light" data-testid="account-title">Hello, {customer.name?.split(" ")[0] || "there"}</h1>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-[11px] text-[#676664]/50 hover:text-[#676664] transition-colors" data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-12">
          <Link to="/account/bookings" className="flex items-center justify-between p-4 bg-white border border-[#282828]/8 hover:border-[#9A7B4F]/30 transition-colors group" data-testid="link-my-bookings">
            <div className="flex items-center gap-3">
              <Package className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
              <span className="text-[12px] text-[#282828]">My Bookings</span>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-[#676664]/30 group-hover:text-[#9A7B4F]" />
          </Link>
          <Link to="/saved-looks" className="flex items-center justify-between p-4 bg-white border border-[#282828]/8 hover:border-[#9A7B4F]/30 transition-colors group" data-testid="link-saved-items">
            <div className="flex items-center gap-3">
              <Heart className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
              <span className="text-[12px] text-[#282828]">Saved Items</span>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-[#676664]/30 group-hover:text-[#9A7B4F]" />
          </Link>
          <Link to="/account/payment-methods" className="flex items-center justify-between p-4 bg-white border border-[#282828]/8 hover:border-[#9A7B4F]/30 transition-colors group" data-testid="link-payment-methods">
            <div className="flex items-center gap-3">
              <CreditCard className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
              <span className="text-[12px] text-[#282828]">Payment Methods</span>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-[#676664]/30 group-hover:text-[#9A7B4F]" />
          </Link>
          <div className="flex items-center gap-3 p-4 bg-white border border-[#282828]/8">
            <Mail className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
            <span className="text-[12px] text-[#676664]/60">{customer.email}</span>
          </div>
        </div>

        {/* Guest Booking Import Prompt */}
        {guestBookingCount > 0 && !guestPromptDismissed && (
          <div className="mb-6 p-5 bg-white border border-[#9A7B4F]/20" data-testid="guest-import-prompt">
            <div className="flex items-start gap-3">
              <Download className="w-5 h-5 text-[#9A7B4F] mt-0.5 shrink-0" strokeWidth={1.5} />
              <div className="flex-1">
                <p className="text-[13px] text-[#282828] font-medium mb-1">
                  We found {guestBookingCount} booking{guestBookingCount > 1 ? "s" : ""} from before you created your account
                </p>
                <p className="text-[12px] text-[#676664]/70 mb-3">Import them to view all your booking details in one place.</p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={async () => {
                      setImportingAll(true);
                      const count = await importAllGuestBookings();
                      setImportingAll(false);
                      if (count > 0) toast.success(`${count} booking${count > 1 ? "s" : ""} imported`);
                    }}
                    disabled={importingAll}
                    className="text-[11px] tracking-[0.15em] uppercase bg-[#9A7B4F] text-white px-5 py-2 hover:bg-[#8B6B3F] transition-colors disabled:opacity-50"
                    data-testid="import-all-btn"
                  >
                    {importingAll ? "Importing..." : "Import All"}
                  </button>
                  <button
                    onClick={dismissGuestPrompt}
                    className="text-[11px] tracking-[0.15em] uppercase text-[#676664]/50 hover:text-[#676664] px-3 py-2"
                    data-testid="dismiss-import-btn"
                  >
                    Not Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <CustomerCredits />

        {/* Import Booking by ID */}
        <div className="mb-8 bg-white border border-[#282828]/8 p-6 lg:p-8" data-testid="import-booking-section">
          <div className="flex items-center gap-2 mb-4">
            <Search className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
            <h2 className="text-sm text-[#282828] font-medium">Import a Guest Booking</h2>
          </div>
          <p className="text-[12px] text-[#676664]/70 mb-4">Have a booking ID from a previous guest checkout? Enter it below to link it to your account. The booking email must match your account email.</p>
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <Input
                value={manualBookingId}
                onChange={(e) => { setManualBookingId(e.target.value); setManualError(""); }}
                placeholder="Enter Booking ID (e.g. BK-20260315-A1B2)"
                className="border-[#282828]/10 text-[13px] py-5"
                data-testid="import-booking-id-input"
              />
              {manualError && (
                <div className="flex items-center gap-1.5 mt-2" data-testid="import-error">
                  <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                  <p className="text-[12px] text-red-600">{manualError}</p>
                </div>
              )}
            </div>
            <button
              onClick={async () => {
                if (!manualBookingId.trim()) { setManualError("Please enter a booking ID"); return; }
                setManualImporting(true);
                setManualError("");
                try {
                  const token = localStorage.getItem("mahra_session_token");
                  const res = await fetch(`${API}/api/customer/guest-bookings/import-by-id`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ booking_id: manualBookingId.trim() }),
                  });
                  if (!res.ok) {
                    const err = await res.json().catch(() => ({}));
                    setManualError(err.detail || "Import failed");
                    return;
                  }
                  const data = await res.json();
                  if (data.imported === 0) { setManualError(data.message || "Already imported"); return; }
                  toast.success("Booking imported successfully");
                  setManualBookingId("");
                } catch { setManualError("Something went wrong. Please try again."); }
                finally { setManualImporting(false); }
              }}
              disabled={manualImporting}
              className="text-[11px] tracking-[0.15em] uppercase bg-[#9A7B4F] text-white px-5 py-2.5 hover:bg-[#8B6B3F] transition-colors disabled:opacity-50 whitespace-nowrap"
              data-testid="import-by-id-btn"
            >
              {manualImporting ? "Importing..." : "Import"}
            </button>
          </div>
        </div>

        {/* Profile Form */}
        <div className="bg-white border border-[#282828]/8 p-6 lg:p-8">
          <div className="flex items-center gap-2 mb-6">
            <User className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
            <h2 className="text-sm text-[#282828] font-medium">Personal Details</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] text-[#676664]/60 tracking-[0.1em] uppercase block mb-1.5">Full Name</label>
              <Input
                value={profile.name}
                onChange={(e) => setProfile(p => ({ ...p, name: e.target.value }))}
                className="border-[#282828]/10 text-[13px] py-5"
                data-testid="profile-name"
              />
            </div>
            <div>
              <label className="text-[10px] text-[#676664]/60 tracking-[0.1em] uppercase block mb-1.5">Phone</label>
              <Input
                value={profile.phone}
                onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))}
                className="border-[#282828]/10 text-[13px] py-5"
                data-testid="profile-phone"
              />
            </div>
            <div className="lg:col-span-2">
              <label className="text-[10px] text-[#676664]/60 tracking-[0.1em] uppercase block mb-1.5">Billing Address</label>
              <Textarea
                value={profile.billing_address}
                onChange={(e) => setProfile(p => ({ ...p, billing_address: e.target.value }))}
                className="border-[#282828]/10 text-[13px] min-h-[80px]"
                data-testid="profile-billing-address"
              />
            </div>
            <div className="lg:col-span-2">
              <label className="text-[10px] text-[#676664]/60 tracking-[0.1em] uppercase block mb-1.5">Shipping Address</label>
              <Textarea
                value={profile.shipping_address}
                onChange={(e) => setProfile(p => ({ ...p, shipping_address: e.target.value }))}
                className="border-[#282828]/10 text-[13px] min-h-[80px]"
                data-testid="profile-shipping-address"
              />
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="mt-6 bg-[#282828] hover:bg-[#1a1a1a] text-white text-[11px] tracking-[0.15em] uppercase px-8 py-5"
            data-testid="save-profile-btn"
          >
            <Save className="w-3.5 h-3.5 mr-2" /> {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
